import { Colors, FontStyleEnums } from '@oy/uicore';
import {
  IconName,
  LogoIcon,
  LogoIconName,
  ProductFeatureIcon,
  ProductFeatureIconName,
  SystemIcon,
} from '@oy/uiicon';
import classNames from 'classnames';
import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { makeId } from '../../../utilities/idComponent';
import { Button } from '../../Components/Button';
import { Text } from '../../Contents/Text';
import Spacer from '../../Layout/Spacer/Spacer';
import styles from './Checkbox.module.css';

export type CheckboxProps = {
  title: string;
  description?: string;
  titleStyle?: FontStyleEnums;
  descriptionStyle?: FontStyleEnums;
  productFeatureIconName?: ProductFeatureIconName;
  logoIconName?: LogoIconName;
  customDescription?: ReactNode;
  customTitle?: ReactNode;
  type?: 'circle' | 'box';
  direction?: 'vertical' | 'horizontal';
  labelId?: string;
  actionButtonLabel?: string;
  onClickActionButton?: () => void;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

function Checkbox({
  type = 'circle',
  checked = false,
  title,
  description,
  titleStyle = FontStyleEnums.BodyMdRegular,
  descriptionStyle = FontStyleEnums.BodyMdRegular,
  customDescription,
  customTitle,
  direction = 'vertical',
  labelId = '',
  id,
  actionButtonLabel,
  productFeatureIconName,
  logoIconName,
  onClickActionButton,
  ...props
}: CheckboxProps) {
  const getIconName = () => {
    const typeStr = type === 'box' ? '-box' : '';
    const checkedStr = checked ? '-selected' : '-unselected';
    const disabledSrt = props.disabled ? '-disabled' : '';
    return `checkmark${typeStr}${checkedStr}${disabledSrt}` as IconName;
  };

  return (
    <label
      className={classNames(styles.container, {
        [styles.alignCenter]:
          !description && !customDescription && !actionButtonLabel,
      })}
      id={makeId('checkboxLabel', props.name, labelId)}
    >
      <input
        type="checkbox"
        className={styles.hiddenCheckbox}
        checked={checked}
        alt={title}
        id={makeId('check', title, id)}
        {...props}
      />
      <div className={styles.styledCheckbox}>
        <SystemIcon
          name={getIconName()}
          color={props.disabled ? Colors.BLACK40 : Colors.BLUE100}
        />
      </div>
      <div>
        <Spacer
          left={Spacer.Size.S}
          className={classNames({
            [styles.horizontal]: direction === 'horizontal',
          })}
        >
          <div className={styles.icon}>
            {(logoIconName || productFeatureIconName) && (
              <Spacer right={Spacer.Size.S}>
                {logoIconName && <LogoIcon name={logoIconName} />}
                {productFeatureIconName && (
                  <ProductFeatureIcon
                    name={productFeatureIconName}
                    color={Colors.BLACK100}
                  />
                )}
              </Spacer>
            )}
            <Text
              textStyle={titleStyle}
              color={props.disabled ? Colors.BLACK40 : Colors.BLACK100}
            >
              {title}
            </Text>
            {customTitle}
          </div>
          {customDescription ||
            (description && (
              <Spacer
                top={direction === 'vertical' ? Spacer.Size.XXXS : undefined}
              >
                <Text textStyle={descriptionStyle}>{description}</Text>
              </Spacer>
            ))}
        </Spacer>
        {actionButtonLabel && onClickActionButton && (
          <Spacer left={Spacer.Size.S}>
            <Button
              className={styles.actionButton}
              size={Button.Size.Small}
              buttonType={Button.Type.Text}
              onClick={onClickActionButton}
            >
              <Text
                textStyle={Text.Style.BodySmSemibold}
                color={Colors.BLUE100}
              >
                {actionButtonLabel}
              </Text>
            </Button>
          </Spacer>
        )}
      </div>
    </label>
  );
}

export default Checkbox;
